import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";
import { useCategories, useDirectory, catName } from "@/lib/hooks";

const URG = ["basse", "normale", "haute", "urgente"];

export function StaffControls({ tk }) {
  const { t, lang } = usePrefs();
  const cats = useCategories();
  const providers = useDirectory("prestataire");
  const qc = useQueryClient();
  const [tri, setTri] = useState({ category: tk.category, urgency: tk.urgency });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ticket", tk.id] }); qc.invalidateQueries({ queryKey: ["tickets"] }); };
  const call = async (fn) => { try { await fn(); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const fit = (p) => Number((p.trades || []).includes(tk.category));
  const sorted = [...providers].sort((a, b) => (b.rating_avg ?? -1) - (a.rating_avg ?? -1) || b.rating_count - a.rating_count || fit(b) - fit(a));
  return (
    <div className="space-y-5">
      <div>
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t("assign")}</div>
        <Select value={tk.assigned_to || ""} onValueChange={(v) => call(() => api.post(`/tickets/${tk.id}/assign`, { provider_id: v }))}>
          <SelectTrigger data-testid="assign-provider-select"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{sorted.map((p) => <SelectItem key={p.id} value={p.id} className="group" data-testid={`assign-option-${p.id}`}>
            <span className="font-semibold">{p.name}</span>{" "}
            {p.rating_avg != null ? <span className="font-data text-[#996515] group-focus:text-inherit" data-testid={`assign-rating-${p.id}`}>★ {p.rating_avg.toFixed(1)} ({p.rating_count})</span> : <span className="text-slate-400 group-focus:text-inherit" data-testid={`assign-rating-${p.id}`}>★ —</span>}
            <span className="text-slate-500 group-focus:text-inherit"> · {(p.trades || []).map(cats.label).join(", ")}</span>
          </SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t("category")} / {t("urgency")}</div>
        <div className="grid grid-cols-2 gap-2">
          <Select value={tri.category} onValueChange={(v) => setTri({ ...tri, category: v })}>
            <SelectTrigger data-testid="triage-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.trade.map((c) => <SelectItem key={c.key} value={c.key}>{catName(c, lang)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={tri.urgency} onValueChange={(v) => setTri({ ...tri, urgency: v })}>
            <SelectTrigger data-testid="triage-urgency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{URG.map((u) => <SelectItem key={u} value={u}>{t(u)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" className="mt-2 w-full rounded-sm" data-testid="triage-validate-button" onClick={() => call(() => api.put(`/tickets/${tk.id}/triage`, tri))}>
          <CheckCheck size={14} className="mr-1" />{t("aiValidate")} {tk.ai_validated && "✓"}
        </Button>
      </div>
    </div>
  );
}
